import { cloneDeep, isEqual, isPlainObject, omit } from 'lodash';
import { type IFramework } from '@/framework';
import type { TextRenderSnapshot } from '@/framework/services/text-render';
import { type Layer } from '@/layers/layer';
import {
    applySnapshotSize,
    applyTextDiff,
    collectFontChars,
    collectFontSubsetRequests,
    createEmptyEditingSnapshot,
    Events,
    requestTextSVGPreview,
    resolveRangeAttrsState,
    type IEdgelessTextLayer,
    type TextLayer,
} from '@/layers/text-layer';
import { refreshFontMaterialIDs } from '@/layers/text-layer/utils/font';
import { estimateLayerSize, measureLayer } from '@/layers/text-layer/utils/measure';
import { base64Kit } from '@/utils/base64-kit';
import { loadImageAsync } from '@/utils/file-kit';
import { setLayerRuntimeValue } from '@/utils/layer-kit';
import { rotatePoint } from '@/utils/math-kit';
import { rangeToUtf16, type TextSelectionRange } from '@/utils/text-kit';
import { LayerType, MaterialIdsType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { EDITOR_PREWARM_CHARS } from '../constants';
import { getEmptyCaretX } from '../utils';
import {
    type TextEditorAdapter,
    type TextEditorContentGeometry,
    type TextEditorDisplayStyle,
    type TextEditorExitContext,
    type TextEditorLayerState,
    type TextEditorPreparedExit,
    type TextEditorUpdateReason,
} from './types';

// Shape Text 最终化期间使用独立 key，避免移除同图层的其它 loading。
const SHAPE_TEXT_FINALIZATION_LOADING_KEY = 'text-editor:shape-text-finalization';

export class TextLayerEditorAdapter implements TextEditorAdapter {
    private shapeTextBaseline: ILayerProps | null = null;

    constructor(private readonly framework: IFramework) {}

    matches(layer: ILayerProps) {
        return layer.layerType === LayerType.TEXT_LAYER;
    }

    getText(layer: ILayerProps) {
        return (layer.data as ITextLayerProps).text;
    }

    /**
     * 读取 Text Layer 供通用编辑视图使用的窄只读样式投影。
     *
     * @param layer - 当前 Text 工作值。
     * @returns 与图层类型无关的文字样式投影。
     */
    getDisplayStyle(layer: ILayerProps): TextEditorDisplayStyle {
        const data = layer.data as ITextLayerProps;

        return {
            fontFamily: data.fontFamily,
            fontSize: data.fontSize,
            lineSpacing: data.lineSpacing,
            letterSpacing: data.letterSpacing,
            bold: data.bold,
            italic: data.italic,
            color: data.color,
        };
    }

    /**
     * 读取 Text Layer 的内容框几何投影。
     *
     * Text Layer 的逻辑盒即文字内容框，因此横排没有额外偏移；竖排排版快照的行内原点
     * 位于内容框右边界，需要补一个 contentWidth 才能落回图层局部坐标系。
     *
     * @param layer - 当前 Text 工作值。
     * @param snapshot - 当前工作值对应的排版快照。
     * @returns 与图层类型无关的内容框几何投影。
     */
    getContentGeometry(layer: ILayerProps, snapshot: TextRenderSnapshot | null): TextEditorContentGeometry {
        const data = layer.data as ITextLayerProps;

        return {
            emptyCaretX: getEmptyCaretX(data.textAlign, layer.width),
            contentOffset: {
                x: snapshot?.writingMode === 'vertical-rl' ? snapshot.contentWidth : 0,
                y: 0,
            },
        };
    }

    resolveRangeAttrsState(layer: ILayerProps, selection: TextSelectionRange) {
        return resolveRangeAttrsState(layer.data as ITextLayerProps, selection);
    }

    /**
     * 从当前文本工作值裁出保留样式的独立图层，不修改会话或画布
     *
     * @param layer - 当前文本图层
     * @param range - UTF-8 字节选区
     * @returns 按内容测量并受画布边界约束的选区图层，空选区返回 null
     */
    createClipboardLayer(layer: ILayerProps, range: TextSelectionRange): ILayerProps | null {
        const source = layer.data as ITextLayerProps;
        // 编辑器选区使用 UTF-8 字节偏移，slice 使用 UTF-16 索引，先转换以免截断中文或 emoji。
        const [start, end] = rangeToUtf16(source.text, range);
        const text = source.text.slice(start, end);
        if (!text) return null;

        // 剪切随后会修改源图层；复制结果需要保留此刻的内容和样式快照。
        let copied = cloneDeep(layer);
        const data = copied.data as ITextLayerProps;

        // 将全文选区换算到每个分段，只保留交集文字；首尾段被裁剪，中间段沿用原样式。
        let offset = 0;
        data.textContents = (data.textContents || []).flatMap((item) => {
            const from = Math.max(start - offset, 0);
            const to = Math.min(end - offset, item.text.length);
            offset += item.text.length;
            return to > from ? [{ ...item, text: item.text.slice(from, to) }] : [];
        });
        data.text = text;

        // 普通单段不再需要分段结构，但需先合并图层默认值与局部覆盖，避免未显式设置的样式丢失。
        // 花字的效果、缩放和偏移属于单字数据，不能随 textContents 一起清空。
        if (data.textContents.length === 1 && !data.advancedTextMaterial) {
            const attrs = resolveRangeAttrsState(source, range);
            if (attrs.type === 'uniform') Object.assign(data, cloneDeep(attrs.attrs));
            data.textContents = [];
        }

        // 与旧版一致，选中文字独立成层时不携带整段列表或变形效果，原截图也不再对应裁剪后的内容。
        data.listStyle = '';
        delete data.listStyleMaterial;
        delete data.shapeExtension;
        delete data.screenshotUrl;

        // 新图层脱离源编辑会话，先按内容自适应尺寸；不能直接沿用源文本框的固定宽度。
        copied.__isOnTextEditing = false;
        copied.__fixedWidth = false;
        delete copied.aiGcOperate;

        // 内容和效果裁剪后同步收敛素材引用，避免仍记录未复制的字体或已移除的列表、变形素材。
        copied = refreshFontMaterialIDs(this.framework.service.font, copied);

        const materialIds = copied.materialIds!;
        delete materialIds[MaterialIdsType.TEXT_LIST];
        delete materialIds[MaterialIdsType.TEXT_TRANSFORM];

        // 划重点既可能设在图层级，也可能设在分段级，两处都需要收集并去重。
        const markIds = [
            ...new Set(
                [data.textMark, ...data.textContents.map((item) => item.textMark)].flatMap((mark) =>
                    mark ? [mark.materialId] : [],
                ),
            ),
        ];
        if (markIds.length) {
            materialIds[MaterialIdsType.TEXT_MARK] = markIds;
        } else {
            delete materialIds[MaterialIdsType.TEXT_MARK];
        }

        // 先测量选中文字的自然尺寸；引擎不可用时沿用已有估算兜底。
        const engine = this.framework.service.textEngine.getEngine();
        let size = engine && !engine.isDisposed ? measureLayer(engine, copied) : estimateLayerSize(copied);

        // 普通画布按当前位置到边缘的剩余空间限宽（竖排限高），无限画布保持自然尺寸。
        if (!this.framework.service.edgelessCanvas.isEdgelessMode) {
            const isVertical = data.writingMode === 'vertical-rl';
            const axis = isVertical ? 'height' : 'width';
            const frameSize = this.framework.service.frame.getActiveFrameSize();
            const availableSize = frameSize[axis] - (isVertical ? copied.top : copied.left);
            if (availableSize > 0 && size[axis] >= availableSize) {
                // 与编辑态一致，达到画布边缘后固定行内尺寸并重新计算换行后的尺寸。
                copied = { ...copied, __fixedWidth: true, [axis]: availableSize };
                if (engine && !engine.isDisposed) size = measureLayer(engine, copied);
                size = { ...size, [axis]: availableSize };
            }
        }

        return { ...copied, width: size.width, height: size.height };
    }

    /**
     * 准备 Text Layer 进入编辑态所需的宿主工作值与 snapshot。
     *
     * @param layer - 请求进入编辑态的真实 Text Layer。
     * @returns 可提交的初始编辑状态；准备失败时返回 null。
     */
    async prepareEnter(layer: ILayerProps): Promise<TextEditorLayerState | null> {
        if (!this.matches(layer)) return null;

        await this.prewarmFonts(layer);

        let editingLayer = layer;

        if ((layer.data as ITextLayerProps).shapeExtension) {
            const textLayer = this.framework.service.registration.getInstance<TextLayer>('TextLayer');
            const candidate = await textLayer?.cancelShapeEffect(layer.id, {
                update: false,
                removeMaterialID: false,
            });
            if (!candidate) return null;

            editingLayer = candidate;
        }

        const { snapshot } = this.resolveLayerState(editingLayer, null, false);
        if (!(layer.data as ITextLayerProps).shapeExtension) {
            editingLayer = this.normalizeTextFrameOnEnter(editingLayer, snapshot);
        }
        const editing = { ...editingLayer, __isOnTextEditing: true };
        return { editingLayer: editing, snapshot };
    }

    /**
     * 进入编辑时按排版高度调整文字框，并保持正文在舞台上的位置
     *
     * 适用于所有普通横排、固定宽度文字，不区分新建文字与旧模板，也不依赖 useEngine。旧 DOM
     * 模板常有冗余高度，但高度差本身才是调整依据。高度一致或快照未就绪时跳过，自适应宽度、
     * 竖排不在此处调整；变形文字进入编辑时产生的临时普通文字由调用方排除。
     *
     * 默认/显式顶部对齐与 Canvas 的 getBaseInfo 保持一致：未旋转时只调整高度，left/top 不变。
     * 旋转围绕图层中心进行，高度变化也会改变旋转中心，因此旋转图层还要补偿 left/top，才能保留
     * 同一个舞台正文起点。显式居中/底部对齐暂沿用原有保持中心的补偿，不在这里扩展其对齐能力。
     *
     * 返回新的编辑工作值，不修改传入图层。选框高度可能改变；即使没有输入就退出，调整结果也会
     * 沿用现有编辑提交、历史和保存流程保留，预览、光标和选区无需另外补偿旧框高度。
     *
     * @param layer - 准备进入编辑的普通文字图层
     * @param snapshot - 进入时已取得的当前排版快照；未就绪时为 null
     * @returns 调整后的编辑工作值，无需调整时返回原图层
     */
    private normalizeTextFrameOnEnter(layer: ILayerProps, snapshot: TextRenderSnapshot | null): ILayerProps {
        if (
            !snapshot ||
            snapshot.writingMode !== 'horizontal-tb' ||
            layer.__fixedWidth === false ||
            layer.height === snapshot.contentHeight
        ) {
            return layer;
        }

        const nextLayer = applySnapshotSize(layer, snapshot);
        const halfHeightDiff = (layer.height - nextLayer.height) / 2;
        const { verticalAlign } = layer.data as ITextLayerProps;
        // 设半个高度差为 d：top + d 先保持原中心，再用旋转后的 (0, -d) 把正文顶部放回原位。
        // 无旋转时两项抵消；有旋转时补偿同时落在 x/y 轴，不能只修改 top。
        const shift =
            !verticalAlign || verticalAlign === 'top'
                ? rotatePoint({ x: 0, y: -halfHeightDiff }, layer.rotate || 0)
                : { x: 0, y: 0 };
        return { ...nextLayer, left: layer.left + shift.x, top: layer.top + halfHeightDiff + shift.y };
    }

    commitEnter(state: TextEditorLayerState, originLayer: ILayerProps) {
        this.shapeTextBaseline = (originLayer.data as ITextLayerProps).shapeExtension
            ? cloneDeep(state.editingLayer)
            : null;
        this.updateEditingLayer(state.editingLayer, state.snapshot);
    }

    /**
     * 将最新文本应用到 Text 工作值，不在输入事件内执行排版。
     *
     * 这里只维护纯文本和富文本 spans 的一致性。尺寸与 snapshot 暂时沿用 session 已发布状态，随后由
     * Manager 合并连续输入并调用 `resolveEditingState()` 重新计算，避免每次 input 都同步执行全文 layout。
     *
     * @param layer - 当前 Text 工作值。
     * @param text - 最新纯文本。
     * @returns 尚未排版的最新 Text 工作值。
     */
    applyText(layer: ILayerProps, text: string): ILayerProps {
        const previous = layer.data as ITextLayerProps;
        const editingLayer: ILayerProps = {
            ...layer,
            data: {
                ...previous,
                text,
                textContents: applyTextDiff(previous, previous.text, text),
            },
        };
        if (previous.text !== text) {
            editingLayer.extraInfo = {
                ...(isPlainObject(layer.extraInfo) ? layer.extraInfo : {}),
                modified: true,
            };
        }
        return editingLayer;
    }

    /**
     * 使用真实 Text 宿主重新解析当前工作值。
     *
     * @param layer - 当前 Text 工作值。
     * @param currentSnapshot - 当前会话最后可用的排版快照。
     * @returns 当前工作值与 fallback 后的 snapshot。
     */
    resolveEditingState(layer: ILayerProps, currentSnapshot: TextRenderSnapshot | null = null): TextEditorLayerState {
        return this.resolveLayerState(layer, currentSnapshot, true);
    }

    /**
     * 同步外部 Text 更新并刷新已有 Preview。
     *
     * @param layer - 外部更新后的 Text 工作值。
     * @param current - 同步前的当前编辑状态。
     * @returns 同步后的工作值与 fallback 后的 snapshot。
     */
    syncLayer(layer: ILayerProps, current: TextEditorLayerState): TextEditorLayerState {
        const state = this.resolveLayerState(layer, current.snapshot, false);
        const { snapshot } = state;
        if (snapshot) requestTextSVGPreview(this.framework, 'refresh', layer);
        return state;
    }

    /**
     * 将尚未排版的最新 Text 工作值立即写入画布数据层。
     *
     * 第三个参数固定为 true，表示只同步数据并保持当前正式 drawable；SVG Preview 与尺寸更新必须等
     * `resolveEditingState()` 产出同输入结果后由 `updateEditingLayer()` 发布。
     *
     * @param layer - 已应用最新文本、尚未排版的 Text 工作值。
     * @returns 无返回值。
     */
    updateEditingData(layer: ILayerProps): void {
        this.framework.service.frame.addOrUpdateLayer(layer, undefined, true);
        this.framework.service.frame.setActiveLayer(layer.id);
    }

    /**
     * 将已排版的编辑图层写入画布并更新预览。
     *
     * snapshot 与 layer 来自同一次 Adapter 解析，并一同交给 Text SVG Preview，避免 Preview 再显式
     * layout 一次。若本轮没有可用 snapshot，Preview 会保留上一份有效画面，数据层仍写入最新文本。
     * shape text 继续沿用其独立的 finish preview 路径。
     *
     * @param layer - 当前编辑图层。
     * @param snapshot - 当前图层对应的 Text Renderer 排版快照；null 表示当前没有可发布结果。
     * @param _reason - 本次更新是用户编辑还是宿主 Text Render Invalidation 后的重解析。
     * @returns 无返回值。
     */
    updateEditingLayer(
        layer: ILayerProps,
        snapshot: TextRenderSnapshot | null,
        _reason?: TextEditorUpdateReason,
    ): void {
        if ((layer.data as ITextLayerProps).shapeExtension) {
            this.finishEditingPreview(layer);
            return;
        }

        // pending/failed 只代表本轮几何尚未 ready，不能把活跃编辑误当成 commit。
        // 仍写入最新 Text 工作值；Preview 自己会在无法构建时保留上一份有效画面。
        //
        // Text 宿主在结果失效时会自行发出 update / refresh（见 EdgelessTextLayer），
        // 因此这里对两种原因保持同一行为，避免改变已经收敛的 Text Layer 交互。
        requestTextSVGPreview(this.framework, 'update', layer, snapshot);
        this.updateEditingData(layer);
    }

    /**
     * 完成 Text Layer 退出事务，并在异步边界后拒绝提交过期图层
     *
     * Shape Text 的 loading、最终截图与一次性 drawable 交接都封装在该方法内；Manager 无需知道
     * Preview、Canvas、frame 和 history 的提交顺序。准备失败且目标仍有效时返回 false 以恢复编辑，
     * 图层删除或锁定则视为无需提交的成功退出。
     *
     * @param context - 退出开始前捕获的 Text 编辑状态
     * @returns 退出已经提交或无需提交时返回 true；可恢复的准备失败返回 false
     */
    async finalizeExit(context: TextEditorExitContext): Promise<boolean> {
        const layerId = context.editingLayer.id;
        const isShapeText = Boolean((context.originLayer.data as ITextLayerProps).shapeExtension);

        const source = this.framework.service.flatLayer.getLayer(layerId);
        if (!source || source.lock) {
            this.resetEditingState();
            return true;
        }
        const sourceBaseline = this.framework.service.flatLayer.getLayer(layerId, true);

        if (isShapeText) {
            this.framework.service.loading.add(layerId, SHAPE_TEXT_FINALIZATION_LOADING_KEY);
        }

        try {
            const prepared = await this.prepareExit({ ...context, editingLayer: source });

            // 体验问题（但其实也可以不改）：等待变形文字图片加载完毕之后再执行后续流程，这样可以避免图层选框先更新，然后内容再更新
            if (isShapeText && prepared?.type === 'commit') {
                const screenshotUrl = (prepared.finalLayer.data as ITextLayerProps).screenshotUrl;
                if (!screenshotUrl) return false;
                try {
                    // 截图生成后仍可能等待上传和图片加载，不能先提交最终选框几何。
                    await loadImageAsync(await base64Kit.getRemoteUrlAsync(screenshotUrl));
                } catch (error) {
                    console.error('Shape Text final image loading failed:', error);
                    return false;
                }
            }

            const current = this.framework.service.flatLayer.getLayer(layerId);
            if (!current || current.lock) {
                this.resetEditingState();
                return true;
            }

            if (!isEqual(this.framework.service.flatLayer.getLayer(layerId, true), sourceBaseline)) return false;
            if (!prepared) return false;

            const drawableSettled =
                isShapeText && prepared.type === 'commit' ? this.waitForDrawableSettled(prepared.finalLayer.id) : null;
            const committed = this.commitPreparedExit(prepared);
            if (drawableSettled && committed) await drawableSettled;
            return committed;
        } finally {
            if (isShapeText) {
                this.framework.service.loading.remove(layerId, SHAPE_TEXT_FINALIZATION_LOADING_KEY);
            }
        }
    }

    private async prepareExit(context: TextEditorExitContext): Promise<TextEditorPreparedExit | null> {
        let latest = this.framework.service.flatLayer.getLayer(context.editingLayer.id, true) ?? context.editingLayer;
        const latestData = latest.data as ITextLayerProps;

        if (latestData.text === '') {
            return { type: 'delete', layerId: latest.id, originLayer: context.originLayer };
        }

        const originData = context.originLayer.data as ITextLayerProps;
        if (originData.shapeExtension) {
            const derivedKeys = ['__isOnTextEditing', '__runtime', 'left', 'top', 'width', 'height'];
            const comparableLayer = omit(latest, derivedKeys);
            const comparableBaseline = this.shapeTextBaseline ? omit(this.shapeTextBaseline, derivedKeys) : null;

            if (comparableBaseline && isEqual(comparableBaseline, comparableLayer)) {
                return {
                    type: 'commit',
                    originLayer: context.originLayer,
                    finalLayer: { ...context.originLayer, __isOnTextEditing: false },
                    hasLayerChange: false,
                };
            }

            if (latest.__fixedWidth !== true) latest = { ...latest, __fixedWidth: true };
            await this.ensureFinalFontSubsets(latest);

            let finalLayer: ILayerProps;
            try {
                finalLayer = await this.framework.service.registration
                    .getInstance<TextLayer>('TextLayer')
                    .refreshShapeTextRender({
                        ...latest,
                        data: { ...latestData, shapeExtension: originData.shapeExtension },
                    });
            } catch (error) {
                console.error('Shape Text finalization failed:', error);
                return null;
            }

            if (!(finalLayer.data as ITextLayerProps).screenshotUrl) return null;

            return {
                type: 'commit',
                originLayer: context.originLayer,
                finalLayer: { ...finalLayer, __isOnTextEditing: false },
                hasLayerChange: true,
            };
        }

        latest = { ...latest, __isOnTextEditing: false };

        // 仅样式变化或排版回填不结束尺寸自适应。
        if (originData.text !== latestData.text) {
            latest.__fixedWidth = true;
        }

        const hasLayerChange = !isEqual(
            omit(context.originLayer, '__isOnTextEditing'),
            omit(latest, '__isOnTextEditing'),
        );

        await this.ensureFinalFontSubsets(latest);
        return {
            type: 'commit',
            originLayer: context.originLayer,
            finalLayer: latest,
            hasLayerChange,
        };
    }

    private commitPreparedExit(prepared: TextEditorPreparedExit): boolean {
        if (prepared.type === 'delete') {
            this.framework.service.registration
                .getInstance<Layer>('Layer')
                .delete(prepared.layerId, prepared.originLayer);
            this.resetEditingState();
            return true;
        }

        const latest = this.framework.service.flatLayer.getLayer(prepared.finalLayer.id);
        if (!latest || latest.lock) {
            this.resetEditingState();
            return true;
        }

        requestTextSVGPreview(this.framework, 'commit', prepared.finalLayer);
        this.framework.service.frame.addOrUpdateLayer(prepared.finalLayer);
        if (prepared.hasLayerChange) {
            this.framework.service.history.write(
                omit(prepared.originLayer, '__isOnTextEditing'),
                omit(prepared.finalLayer, '__isOnTextEditing'),
            );
        }
        this.resetEditingState();
        return true;
    }

    /**
     * 等待目标图层下一轮正式 drawable 完成
     *
     * 订阅必须先于 frame 提交建立，确保当前正式资源无论成功或失败都会结束本轮退出等待；
     * EdgelessObject 的资源版本门会过滤更早的 stale load，因此这里只消费目标图层下一条 settled。
     *
     * @param layerId - 正在完成退出提交的 Text Layer ID
     * @returns 正式 drawable 是否准备成功
     */
    private waitForDrawableSettled(layerId: string): Promise<boolean> {
        return new Promise((resolve) => {
            const handler = this.framework.service.events.on(
                Events.DrawableSettled,
                (settledLayerId: string, ready: boolean) => {
                    if (settledLayerId !== layerId) return;
                    this.framework.service.events.off(handler);
                    resolve(ready);
                },
            );
        });
    }

    private resetEditingState() {
        this.shapeTextBaseline = null;
    }

    /**
     * 通过真实 Edgeless Text 宿主解析四态结果并执行编辑态 fallback。
     *
     * @param layer - 当前 Text 工作值。
     * @param currentSnapshot - 当前 session 最后可用的 snapshot。
     * @param applySize - ready/empty 时是否把当前 snapshot 尺寸应用到工作值。
     * @returns 可供 Text Editor 直接消费的图层与 snapshot。
     */
    private resolveLayerState(
        layer: ILayerProps,
        currentSnapshot: TextRenderSnapshot | null,
        applySize: boolean,
    ): TextEditorLayerState {
        const host = this.framework.service.edgelessCanvas.getByLayerId(layer.id) as IEdgelessTextLayer | null;
        if (!host) return { editingLayer: layer, snapshot: currentSnapshot };

        let resolution = host.layout(layer);
        if (
            applySize &&
            resolution.status === 'ready' &&
            layer.__fixedWidth === false &&
            !this.framework.service.edgelessCanvas.isEdgelessMode
        ) {
            const isVertical = (layer.data as ITextLayerProps).writingMode === 'vertical-rl';
            const frameSize = this.framework.service.frame.getActiveFrameSize();
            const availableSize = isVertical ? frameSize.height - layer.top : frameSize.width - layer.left;
            const snapshot = resolution.value;
            const inlineSize =
                (isVertical ? snapshot.contentHeight : snapshot.contentWidth) + snapshot.listMarkerIndent;
            if (availableSize > 0 && inlineSize >= availableSize) {
                // 使用当前位置到画布边缘的剩余尺寸，重新排版后再一起提交约束与几何。
                const constrained = { ...layer, __fixedWidth: true, [isVertical ? 'height' : 'width']: availableSize };
                resolution = host.layout(constrained);

                if (resolution.status !== 'ready') return { editingLayer: layer, snapshot: currentSnapshot };
                layer = constrained;
            }
        }
        let snapshot: TextRenderSnapshot | null;
        let isCurrentResolution = false;

        // ready/empty 都描述当前输入，可以更新本轮编辑几何；pending/failed 必须保留 session 已接受的快照。
        if (resolution.status === 'ready') {
            snapshot = resolution.value;
            isCurrentResolution = true;
        } else if (resolution.status === 'empty') {
            snapshot = createEmptyEditingSnapshot(layer);
            isCurrentResolution = true;
        } else {
            snapshot = currentSnapshot;
        }

        return {
            editingLayer: applySize && snapshot && isCurrentResolution ? applySnapshotSize(layer, snapshot) : layer,
            snapshot,
        };
    }

    private finishEditingPreview(layer: ILayerProps, { activate = true }: { activate?: boolean } = {}) {
        requestTextSVGPreview(this.framework, 'commit', layer);
        this.framework.service.frame.addOrUpdateLayer(layer);
        if (activate) this.framework.service.frame.setActiveLayer(layer.id);
    }

    private async prewarmFonts(layer: ILayerProps) {
        const requests = collectFontChars(layer, { prewarmChars: EDITOR_PREWARM_CHARS });
        await Promise.allSettled(
            [...requests].map(async ([fontFamily, chars]) => {
                const font = this.framework.service.font;
                if (!font.getByFamily(fontFamily) || font.hasRegisteredFallback(fontFamily)) return null;
                const subsetFamily = await font.loadSubset(fontFamily, chars);
                if (!subsetFamily) return null;

                setLayerRuntimeValue(layer, {
                    fontFamilyMap: {
                        ...(layer.__runtime?.fontFamilyMap || {}),
                        [fontFamily]: subsetFamily,
                    },
                });
                return subsetFamily;
            }),
        );
    }

    private async ensureFinalFontSubsets(layer: ILayerProps) {
        const requests = collectFontSubsetRequests(layer).filter(({ fontFamily, chars }) => {
            if (!this.framework.service.font.getByFamily(fontFamily)) return false;
            const subsetName = layer.__runtime?.fontFamilyMap?.[fontFamily];
            return !this.framework.service.font.hasAvailableSubset(subsetName, chars);
        });
        if (requests.length === 0) return;

        await Promise.allSettled(
            requests.map(async ({ fontFamily, chars }) => {
                const subsetFamily = await this.framework.service.font.loadSubset(fontFamily, chars);
                if (!subsetFamily) return null;

                setLayerRuntimeValue(layer, {
                    fontFamilyMap: {
                        ...(layer.__runtime?.fontFamilyMap || {}),
                        [fontFamily]: subsetFamily,
                    },
                });
                return subsetFamily;
            }),
        );
    }
}
